import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Search, CheckCircle2, XCircle, ScanLine, User as UserIcon } from "lucide-react";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const SCANNER_ID = "pos-qr-reader";

export default function PosScanner() {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!scanning) return;
    const scanner = new Html5Qrcode(SCANNER_ID);
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 240 },
        (decodedText) => handleScan(decodedText),
        () => {},
      )
      .catch(() => setError("Could not access the camera. Use manual search instead."));
    return () => {
      // html5-qrcode throws SYNCHRONOUSLY ("Cannot stop, scanner is not running
      // or paused") when stop() is called before start() succeeded — a bare
      // .catch() does not trap that, so the throw escapes the effect cleanup
      // and unmounts the whole POS app. Guard both paths.
      const safeClear = () => { try { scanner.clear(); } catch { /* noop */ } };
      try {
        const p = scanner.stop();
        if (p && typeof p.catch === "function") p.catch(() => {}).finally(safeClear);
        else safeClear();
      } catch {
        safeClear();
      }
    };
  }, [scanning]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopScanning = async () => {
    setScanning(false);
    try { await scannerRef.current?.stop(); } catch { /* noop */ }
  };

  const handleScan = async (qr_token) => {
    if (busyRef.current) return;
    busyRef.current = true;
    await stopScanning();
    try {
      const { data } = await apiClient.post("/pos/scan", { qr_token });
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, message: getApiErrorMessage(e, "Could not process this scan.") });
    } finally {
      busyRef.current = false;
    }
  };

  const scanByCustomer = async (customer_id) => {
    try {
      const { data } = await apiClient.post("/pos/scan-by-customer", { customer_id });
      setResult({ ok: true, ...data });
      setManualOpen(false);
      setScanning(false);
    } catch (e) {
      setResult({ ok: false, message: getApiErrorMessage(e, "Could not process this pickup.") });
      setManualOpen(false);
      setScanning(false);
    }
  };

  const search = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) { setMatches([]); return; }
    try {
      const { data } = await apiClient.get("/pos/customers/search", { params: { q } });
      setMatches(data);
    } catch { setMatches([]); }
  };

  const reset = () => {
    setResult(null);
    setError("");
    setManualOpen(false);
    setQuery("");
    setMatches([]);
    setScanning(true);
  };

  return (
    <div className="flex flex-col items-center" data-testid="pos-scanner-page">
      {!result && !manualOpen && (
        <>
          <p className="mb-3 flex items-center gap-1.5 text-sm font-medium text-text-main">
            <ScanLine size={16} strokeWidth={1.5} /> Point the camera at the customer's QR code
          </p>
          <div id={SCANNER_ID} className="aspect-square w-full max-w-xs overflow-hidden rounded-md border-2 border-border bg-black" data-testid="pos-scanner-camera" />
          {error && <p className="mt-3 text-sm text-danger" data-testid="pos-scanner-camera-error">{error}</p>}
          <button
            type="button" onClick={() => { stopScanning(); setManualOpen(true); }}
            className="mt-4 flex items-center gap-1.5 text-sm font-medium text-text-muted underline"
            data-testid="pos-scanner-manual-fallback-button"
          >
            <Search size={14} strokeWidth={1.5} /> Camera not working? Search by name/phone
          </button>
        </>
      )}

      {manualOpen && !result && (
        <div className="w-full max-w-xs" data-testid="pos-scanner-manual-search">
          <Input
            autoFocus placeholder="Search name or phone..." value={query}
            onChange={(e) => search(e.target.value)} data-testid="pos-scanner-manual-search-input"
          />
          <div className="mt-2 space-y-2">
            {matches.map((m) => (
              <button
                key={m.id} type="button" onClick={() => scanByCustomer(m.id)}
                className="flex w-full items-center gap-2 rounded-md border border-border bg-surface p-3 text-left"
                data-testid={`pos-scanner-manual-result-${m.id}`}
              >
                <UserIcon size={16} strokeWidth={1.5} className="text-text-muted" />
                <div>
                  <p className="text-sm font-medium text-text-main">{m.name}</p>
                  <p className="text-xs text-text-muted">{m.phone}</p>
                </div>
              </button>
            ))}
          </div>
          <button type="button" onClick={reset} className="mt-4 text-xs text-text-muted underline" data-testid="pos-scanner-manual-cancel-button">
            Back to camera
          </button>
        </div>
      )}

      {result && (
        <div className="w-full max-w-xs rounded-md border border-border bg-surface p-5 text-center" data-testid="pos-scan-result">
          {result.ok ? (
            <>
              <CheckCircle2 size={40} strokeWidth={1.5} className="mx-auto text-success" />
              <p className="mt-3 text-base font-semibold text-text-main" data-testid="pos-scan-customer-name">{result.customer_name}</p>
              <p className="mt-1 text-sm text-success">1 tiffin deducted</p>
              <p className="mt-1 text-sm text-text-muted" data-testid="pos-scan-remaining">
                {result.tiffins_remaining} of {result.tiffins_total} remaining
              </p>
            </>
          ) : (
            <>
              <XCircle size={40} strokeWidth={1.5} className="mx-auto text-danger" />
              <p className="mt-3 text-sm font-medium text-danger" data-testid="pos-scan-error-message">{result.message}</p>
            </>
          )}
          <Button className="mt-5 w-full" onClick={reset} data-testid="pos-scan-next-button">Scan Next Customer</Button>
        </div>
      )}
    </div>
  );
}
